import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faBoxes } from '@fortawesome/pro-solid-svg-icons/faBoxes';

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { AuctaneFulfillment, ShipEngine } from '../../../../constants/connection-types';
import UserDateFormat from '../../../../components/user-date-format/index';
import UserTimeFormat from '../../../../components/user-time-format/connected';
import classnames from 'classnames';

import { LogService } from '../../../../services/log-service';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import Global from '../../../../store/global-selectors';
import DataDrivenLogo from '../../../../components/data-driven-logo';
import { Active, Inactive, Disabled } from './constants/inventory-sync-status';
import messages from './messages';
import style from './style.module.less';

const StatusIcon = ({ status }) => {
  if (status === Active) {
    return <FontAwesomeIcon className={style['status-active']} icon={faCheck} />;
  }
  if (status === Inactive || status === Disabled) {
    return (
      <Tooltip>
        <div>
          <FontAwesomeIcon className={style['status-warning']} icon={faExclamationCircle} />
        </div>
        <TooltipContent>
          <div>
            <FormattedMessage {...messages.inactiveSync} />
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }
  LogService.logError(new Error('Unexpected Consolidated Inventory connection status'), { status });
  return null;
};

StatusIcon.propTypes = {
  status: PropTypes.string.isRequired,
};

const TableRow = ({ counts, isGroupFooter, isFirstInGroup }) => {
  const marketPlaces = useSelector(Global.selectReferenceDatagetMarketplaces);
  const { connectionType, connectionStatus, lastUpdated, availableQuantity, isConnectionIncluded } = counts;
  const connectionName = connectionType === ShipEngine ? <FormattedMessage {...messages.shipstationInventory} /> : counts.connectionName;

  const connectionLogo = useMemo(() => {
    if (!isFirstInGroup) {
      return null;
    }
    if (connectionType === AuctaneFulfillment) {
      return <FontAwesomeIcon className={style['store-icon']} icon={faBoxes} />;
    }

    const targetConnectionType = connectionType === ShipEngine ? 'ShipStation' : connectionType;
    const sourceMarketplace = marketPlaces.find((mrk) => mrk.name === targetConnectionType);

    if (sourceMarketplace) {
      return <DataDrivenLogo className={style['store-icon']} logoType='icon' marketplaceId={sourceMarketplace.marketplaceId} />;
    }
    return null;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [marketPlaces, connectionType]);
  return (
    <div
      className={classnames(style['grid-row'], {
        [style['group-footer']]: isGroupFooter,
      })}
    >
      <div
        className={classnames(style['grid-source'], {
          [style['no-icon-name']]: !isFirstInGroup,
        })}
      >
        {connectionLogo}
        {connectionName}
      </div>
      <div className={classnames(style['grid-item'], style.status)}>
        <StatusIcon status={connectionStatus} />
      </div>
      <div className={classnames(style['grid-item'], style.status)}>
        {isConnectionIncluded && <FontAwesomeIcon className={style['status-active']} icon={faCheck} />}
      </div>
      <div className={style['grid-lastUpdated']}>
        <UserDateFormat value={lastUpdated} /> <UserTimeFormat value={lastUpdated} />
      </div>
      <div className={style['grid-item']}>{availableQuantity}</div>
    </div>
  );
};

TableRow.propTypes = {
  counts: PropTypes.shape({
    allocatedQuantity: PropTypes.number.isRequired,
    availableQuantity: PropTypes.number.isRequired,
    committedQuantity: PropTypes.number.isRequired,
    connectionId: PropTypes.string.isRequired,
    connectionName: PropTypes.string.isRequired,
    connectionStatus: PropTypes.string.isRequired,
    isConnectionIncluded: PropTypes.bool.isRequired,
    connectionType: PropTypes.string.isRequired,
    lastUpdated: PropTypes.string.isRequired,
    onhandQuantity: PropTypes.number.isRequired,
  }).isRequired,
  isGroupFooter: PropTypes.bool.isRequired,
  isFirstInGroup: PropTypes.bool.isRequired,
};

export default TableRow;
